import React, { useState } from "react";
import { ActivityFeedItem, User } from "../../api/types.ts";
import {
  useLikeActivityFeedItem,
  useUnlikeActivityFeedItem,
} from "../../api/hooks.ts";
import { ActivityExerciseList } from "./ActivityExerciseList.tsx";
import { Spinner } from "../widgets/Spinner.tsx";
import { FaUserCircle } from "react-icons/fa";
import { RiHandHeartLine } from "react-icons/ri";
import { formatDate } from "../../util/date.ts";
import { Card } from "../containers/Card.tsx";
import { Tooltip } from "../widgets/Tooltip.tsx";
import { ReadMore } from "../widgets/ReadMore.tsx";

interface ActivityFeedCardProps {
  activityFeedItem: ActivityFeedItem;
  profile?: Partial<User>;
  friend?: Partial<User>;
  timeFormat?: "12h" | "24h";
}

const ActivityFeedCard: React.FC<ActivityFeedCardProps> = ({
  activityFeedItem,
  timeFormat,
  profile,
  friend,
}) => {
  const likeActivityFeedItemMutation = useLikeActivityFeedItem();
  const unlikeActivityFeedItemMutation = useUnlikeActivityFeedItem();

  const handleLikeClick = () => {
    if (!profile?.id) return;

    if (activityFeedItem.liked_by.find((like) => like.user_id === profile.id)) {
      unlikeActivityFeedItemMutation.mutate(activityFeedItem.id);
    } else if (
      !activityFeedItem.liked_by.find((like) => like.user_id === profile.id)
    ) {
      likeActivityFeedItemMutation.mutate(activityFeedItem.id);
    }
  };

  const [showLikes, setShowLikes] = useState(false);

  return (
    <Card>
      <Card.Header>
        {/* Header */}
        <div className="flex flex-col w-full">
          <div className="flex flex-col gap-2">
            <span className="text-gray-600 text-sm flex flex-row items-center mx-2">
              <FaUserCircle className="mr-2" />
              {friend?.name ?? <Spinner className="mx-1" />} (
              {friend?.email ?? <Spinner className="mx-1" />})
            </span>
            <h4 className="font-semibold text-lg p-1 m-1">
              {activityFeedItem.name}
            </h4>
          </div>
          <div className="flex flex-col gap-2 pb-2 mx-2">
            <span className="text-sm">
              {formatDate(
                new Date(activityFeedItem.executed_at),
                timeFormat ?? "24h",
              )}
            </span>
          </div>
          {activityFeedItem.description && (
            <ReadMore
              id={activityFeedItem.id}
              text={activityFeedItem.description}
              className="text-sm text-gray-600 text-balance"
            />
          )}
        </div>
      </Card.Header>
      <Card.Content>
        <ActivityExerciseList
          resourceId={activityFeedItem.id}
          activityExercises={activityFeedItem.exercises}
        />
      </Card.Content>

      <Card.Footer>
        {/* Footer stats */}
        <span className="flex flex-row justify-between text-sm text-gray-600">
          <span className="font-medium">
            {activityFeedItem.exercises.length} exercise
            {activityFeedItem.exercises.length !== 1 ? "s" : ""}
          </span>
          <div className="font-medium flex flex-for gap-2 items-center">
            <button
              onClick={handleLikeClick}
              className={
                likeActivityFeedItemMutation.isPending ||
                unlikeActivityFeedItemMutation.isPending
                  ? "pointer-events-none opacity-70"
                  : ""
              }
            >
              <RiHandHeartLine
                className={
                  profile?.id &&
                  activityFeedItem.liked_by.find(
                    (like) => like.user_id === profile.id,
                  )
                    ? "text-primary"
                    : ""
                }
              />
            </button>
            <Tooltip
              label={activityFeedItem.liked_by
                .map((like) => like.name)
                .join(", ")}
              show={activityFeedItem.liked_by.length > 0 && showLikes}
            >
              <span
                onMouseEnter={() => setShowLikes(true)}
                onMouseLeave={() => setShowLikes(false)}
              >
                {activityFeedItem.liked_by.length} like
                {activityFeedItem.liked_by.length !== 1 ? "s" : ""}
              </span>
            </Tooltip>
          </div>
        </span>
      </Card.Footer>
    </Card>
  );
};

export { ActivityFeedCard };
